import { useEffect, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiPost, apiGet } from './client'
import type { BracketFit, CardSearchRequest, CardSearchResponse, CompleteAnalysis, IdentifyCardResponse, OwnershipSummary, RulesChatRequest, RulesChatResponse } from './types'
import type { Lang } from '../i18n/languages'
import { autocompleteQuery } from '../lib/offlineCards/fallback'

// --- Bracket Analyzer ---

interface AnalyzeRequest {
  decklist: string
  commander?: string
}

export function useAnalyzeDeck() {
  return useMutation({
    mutationFn: (req: AnalyzeRequest) =>
      apiPost<CompleteAnalysis>('/decks/analyze-complete', req),
  })
}

// --- Card Search (plain English or syntax) ---

// A mutation, not a query: each search is a deliberate submit, and the server
// charges a translation for a plain-English ask. Keying it as a query would let
// a re-render re-fire it.
//
// The mutation does NOT sequence repeat mutate() calls, so two overlapping
// searches can settle out of order and a slower OLDER response can overwrite
// the newer one on screen. Every submit therefore takes a monotonically
// increasing id, and only the settlement whose id is still the latest is
// rendered. Stale settlements are dropped, never shown. The returned shape
// mirrors the mutation's (mutate / reset / isPending / isError / error / data)
// so callers are unaware of the bookkeeping.
export interface CardSearchState {
  mutate: (req: CardSearchRequest) => void
  reset: () => void
  isPending: boolean
  isError: boolean
  error: unknown
  data: CardSearchResponse | undefined
}

export function useCardSearch(): CardSearchState {
  const mutation = useMutation({
    mutationFn: (req: CardSearchRequest) =>
      apiPost<CardSearchResponse>('/cards/search', req),
  })
  const latestRequestId = useRef(0)
  // The rendered view of the LATEST submission only.
  const [view, setView] = useState<{
    pending: boolean
    data?: CardSearchResponse
    error?: unknown
  }>({ pending: false })

  // mutateAsync, not the per-call onSuccess/onError options: the observer keeps
  // ONE options slot, so a superseded call's callbacks are not guaranteed to be
  // the ones that fire. The promise a mutateAsync call returns is unambiguously
  // that call's, which is what the id has to be compared against.
  const mutate = (req: CardSearchRequest) => {
    const requestId = (latestRequestId.current += 1)
    setView({ pending: true })
    void mutation.mutateAsync(req).then(
      data => {
        if (requestId !== latestRequestId.current) return
        setView({ pending: false, data })
      },
      error => {
        if (requestId !== latestRequestId.current) return
        setView({ pending: false, error })
      },
    )
  }

  const reset = () => {
    // Orphans anything still in flight, so a response arriving after a close
    // cannot repopulate a panel the user already dismissed.
    latestRequestId.current += 1
    setView({ pending: false })
    mutation.reset()
  }

  return {
    mutate,
    reset,
    isPending: view.pending,
    isError: view.error !== undefined,
    error: view.error ?? null,
    data: view.data,
  }
}

// --- Card Autocomplete ---

function defaultAutocomplete(q: string): Promise<string[]> {
  return apiGet<string[]>(`/cards/autocomplete?q=${encodeURIComponent(q)}`)
}

export function useCardAutocomplete(query: string) {
  const q = useQuery({
    ...autocompleteQuery(query, defaultAutocomplete),
    enabled: query.length >= 2,
    staleTime: 60_000,
    // The last answer stays on screen while the next query is in flight, so
    // the list never blanks between keystrokes. isPlaceholderData says the
    // rows are the PREVIOUS query's (the deck editor's Enter waits then).
    placeholderData: keepPreviousData,
  })
  // The callers read `data` as the names, exactly as before the fallback.
  return { ...q, data: q.data?.names, offline: q.data?.offline === true }
}

// Cache all commander names locally for instant autocomplete. Invalidated by a
// server version token (set releases add commanders) with a 7-day TTL backstop,
// so a freshly legal commander shows up on the next visit instead of up to 7
// days later (stale-while-revalidate: serve the cache instantly, refresh in the
// background when the token changed).
const COMMANDER_NAMES_KEY = 'cb-commander-names'
const COMMANDER_NAMES_TS_KEY = 'cb-commander-names-ts'
const COMMANDER_NAMES_VER_KEY = 'cb-commander-names-ver'
const COMMANDER_NAMES_TTL_MS = 7 * 24 * 60 * 60 * 1000

let _commanderNamesCache: string[] | null = null
let _commanderNamesPromise: Promise<string[]> | null = null
let _commanderNamesRevalidated = false

// Subscribers (mounted autocomplete hooks) are notified when a background
// refresh actually changes the catalog, so an already-typed query updates live
// instead of staying stale until the next reload.
const _commanderNamesListeners = new Set<() => void>()
function subscribeCommanderNames(fn: () => void): () => void {
  _commanderNamesListeners.add(fn)
  return () => {
    _commanderNamesListeners.delete(fn)
  }
}
function notifyCommanderNamesUpdated(): void {
  for (const fn of _commanderNamesListeners) {
    try {
      fn()
    } catch {
      // a listener error must not break the refresh
    }
  }
}
function sameNameList(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

async function getCommanderNames(): Promise<string[]> {
  if (_commanderNamesCache) return _commanderNamesCache

  // Serve from localStorage instantly (within the TTL backstop), then revalidate
  // against the server version token in the background.
  const cached = localStorage.getItem(COMMANDER_NAMES_KEY)
  const cachedTime = localStorage.getItem(COMMANDER_NAMES_TS_KEY)
  if (cached && cachedTime) {
    const age = Date.now() - parseInt(cachedTime)
    if (age < COMMANDER_NAMES_TTL_MS) {
      try {
        _commanderNamesCache = JSON.parse(cached)
        void revalidateCommanderNames()
        return _commanderNamesCache!
      } catch {
        // corrupt cache - fall through to a fresh fetch
      }
    }
  }

  // Fetch from API (deduplicate concurrent calls via shared promise).
  // Failures THROW instead of resolving []: resolving an empty catalog as
  // success let React Query cache "no suggestions" under staleTime: Infinity,
  // so an autocomplete whose first fetch died (cold worker, deploy window)
  // stayed dead for those query strings until a full reload. A thrown error
  // gets React Query's retry-with-backoff instead.
  if (!_commanderNamesPromise) {
    _commanderNamesPromise = fetchAndStoreCommanderNames().catch((err: unknown) => {
      _commanderNamesPromise = null
      throw err
    })
  }
  return _commanderNamesPromise
}

// Fetch the version BEFORE the list, so a set update landing mid-flight just
// triggers a harmless re-fetch next visit (never a stuck-stale newer token
// pinned onto an older list).
async function fetchAndStoreCommanderNames(): Promise<string[]> {
  let version: string | null = null
  try {
    version = (await apiGet<{ version: string }>('/cards/commander-names-version')).version
  } catch {
    // version endpoint unavailable - degrade to TTL-only behavior
  }
  const previous = _commanderNamesCache
  const names = await apiGet<string[]>('/cards/commander-names')
  _commanderNamesCache = names
  try {
    localStorage.setItem(COMMANDER_NAMES_KEY, JSON.stringify(names))
    localStorage.setItem(COMMANDER_NAMES_TS_KEY, String(Date.now()))
    if (version) localStorage.setItem(COMMANDER_NAMES_VER_KEY, version)
    else localStorage.removeItem(COMMANDER_NAMES_VER_KEY)
  } catch {
    // storage full / disabled - the in-memory cache still serves this session
  }
  // A background refresh that changed the catalog must re-run any active query.
  if (previous && !sameNameList(previous, names)) notifyCommanderNamesUpdated()
  return names
}

async function revalidateCommanderNames(): Promise<void> {
  if (_commanderNamesRevalidated) return
  _commanderNamesRevalidated = true
  try {
    const { version } = await apiGet<{ version: string }>('/cards/commander-names-version')
    if (version && version !== localStorage.getItem(COMMANDER_NAMES_VER_KEY)) {
      await fetchAndStoreCommanderNames()
    }
  } catch {
    _commanderNamesRevalidated = false // transient failure - retry next session
  }
}

function normalizeForSearch(s: string): string {
  // Lowercase, strip non-word non-space chars (apostrophes, commas, slashes,
  // hyphens, periods), collapse whitespace. Lets "yshtola" match
  // "Y'shtola, Night's Blessed" so users don't have to type punctuation.
  // Mirrors precons.py:normalize_for_search on the server.
  return s.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim()
}

export function useCommanderAutocomplete(query: string) {
  const queryClient = useQueryClient()
  // Warm the catalog while the user is still reading the page. Without this
  // the ~100KB two-round-trip download starts only after the first debounced
  // keystroke, which on a cold worker reads as "autocomplete is broken".
  // Errors are swallowed here on purpose - the query path below owns retries.
  useEffect(() => {
    void getCommanderNames().catch(() => {})
  }, [])
  // When a background refresh changes the catalog, re-run the active filter so a
  // freshly added commander shows up without a reload.
  useEffect(
    () =>
      subscribeCommanderNames(() => {
        void queryClient.invalidateQueries({ queryKey: ['autocomplete-commanders'] })
      }),
    [queryClient],
  )
  return useQuery({
    queryKey: ['autocomplete-commanders', query],
    queryFn: async () => {
      const names = await getCommanderNames()
      if (!query || query.length < 2) return []
      const needle = normalizeForSearch(query)
      if (!needle) return []
      return names
        .filter(n => normalizeForSearch(n).includes(needle))
        .slice(0, 10)
    },
    enabled: query.length >= 2,
    staleTime: Infinity, // Local filtering, no need to refetch
  })
}

// --- Commander Profile ---

export interface CommanderProfile {
  commander_profile: {
    name: string
    color_identity: string[]
    primary_mechanics: string[]
    secondary_mechanics: string[]
    keywords: string[]
    cost_modifiers: unknown[]
    trigger_modifiers: unknown[]
    zone_interactions: string[]
    tribal_types: string[]
    build_axes: string[]
    primary_build_axis: string
    /** Non-English responses only (i18n 2e). Render-only: the identity
     *  fields above stay English (deduped and grouped on). Index-aligned. */
    build_axes_display?: string[]
    primary_build_axis_display?: string
    expected_engines: string[]
    power_level: string | number
    synergy_cards: Record<string, string[]>
    /** Ranked EDHREC themes for this commander (display value + slug + card count). */
    edhrec_themes?: { theme: string; slug: string; count: number }[]
    /** EDHREC-derived v2 engine families. Present when expected_engines_source is "edhrec". */
    expected_engine_families?: string[]
    /** Provenance of the expected engines: "edhrec" (data-driven) or "oracle" (rules-text guess). */
    expected_engines_source?: 'edhrec' | 'oracle'
  }
  card_data: {
    name: string
    mana_cost: string
    type_line: string
    oracle_text: string
    color_identity: string[]
  }
  schema_version: string
}

export function useCommanderProfile(name: string) {
  return useQuery({
    queryKey: ['commander-profile', name],
    queryFn: () => apiGet<CommanderProfile>(`/synergy/commander-profile?name=${encodeURIComponent(name)}`),
    enabled: !!name,
    staleTime: 300_000,
  })
}

// --- Build and Analyze ---

export interface BuildAndAnalyzeRequest {
  commander: string
  power_level?: number
  budget?: number
  partner?: string
  archetype?: string
  tribal_focus?: string
  win_condition?: string
  theme_focus?: string
  /** EDHREC strategy slug from a `kind: 'strategy'` sub-theme option. */
  strategy_focus?: string
  interaction_level?: string
  exclude_game_changers?: boolean
  exclude_tutors?: boolean
  exclude_fast_mana?: boolean
  collection_id?: string
  collection_only?: boolean
  prefer_owned?: boolean
  additional_owned?: string[]
  /** Cards the build must include. Server cap is 100 names; each is resolved
   *  through Scryfall and checked for legality and colour identity, and
   *  anything it cannot honour comes back in summary.unmet_criteria. */
  include_cards?: string[]
}

export interface BuildOption {
  value: string
  label: string
  /**
   * Sub-theme options only. `tag` is a static mechanic flavor and travels as
   * `theme_focus`; `strategy` is an EDHREC theme published for the chosen
   * commander and travels as `strategy_focus`. Optional because the other
   * option lists (win conditions, interaction levels) carry no kind.
   */
  kind?: 'tag' | 'strategy'
  /** EDHREC deck count behind a `strategy` option. Null on tags. */
  deck_count?: number | null
}

export interface Archetype {
  id: string
  label: string
  category: string
  needs_tribe: boolean
}

export interface BuildOptions {
  archetypes: Archetype[]
  win_conditions: BuildOption[]
  themes: BuildOption[]
  interaction_levels: BuildOption[]
}

/**
 * Builder dropdown options. Passing a commander makes the sub-theme list
 * commander-aware: the themes EDHREC publishes for that commander come back
 * ahead of the static mechanic tags. Without one the response is the plain
 * tag list, so the hook is still safe to call before a commander is chosen.
 */
export function useBuildOptions(commander?: string, partner?: string) {
  const params = new URLSearchParams()
  // eslint-disable-next-line i18next/no-literal-string -- URL param name
  if (commander) params.set('commander', commander)
  // eslint-disable-next-line i18next/no-literal-string -- URL param name
  if (partner) params.set('partner', partner)
  const qs = params.toString()
  return useQuery({
    // Commander and partner are part of the key: without them a second
    // commander would be served the first one's themes from cache.
    queryKey: ['build-options', commander ?? '', partner ?? ''],
    queryFn: () => apiGet<BuildOptions>(`/commander/build-options${qs ? `?${qs}` : ''}`),
    staleTime: 1000 * 60 * 60, // 1 hour - rarely changes
  })
}

export interface PopularCommander {
  name: string
  slug: string
}

/**
 * Most-built commanders, seeding the builder's empty state.
 *
 * The server fails closed on a missing artifact and returns an empty list, so
 * an empty result is a normal state here, not an error - the grid simply does
 * not render and the search above it still works. No retry for the same
 * reason: this is decorative, and a retry storm on a degraded server buys
 * nothing.
 */
export function usePopularCommanders(limit = 12) {
  return useQuery({
    queryKey: ['popular-commanders', limit],
    queryFn: async () =>
      (await apiGet<{ count: number; commanders: PopularCommander[] }>(
        `/commander/popular?limit=${limit}`,
      )).commanders,
    staleTime: 1000 * 60 * 60 * 24, // the artifact only changes on a rebuild
    retry: false,
  })
}

interface DeckCard {
  name: string
  quantity: number
  role: string
  reason: string
  cmc?: number
  price_usd?: number
  in_collection?: boolean
  tcgplayer_id?: number
  type_line?: string
  card_type?: string
  /** EDHREC per-card synergy as a fraction (e.g. 0.39 = +39%). null/absent for
   *  non-EDHREC cards (staples, tutors, lands). */
  synergy?: number
  /** otag-derived display bucket, see CardClassification.function_category. */
  function_category?: string
}

export interface BuildAndAnalyzeResult {
  session_id?: string
  /** The language the analysis prose was rendered in, set by the API ONLY on
   *  a non-English response (mtg-api i18n 3-api.1), at the root AND inside
   *  `analysis`. Absent means English. Never stamped by the client. */
  analysis_locale?: Exclude<Lang, 'en'>
  summary: {
    headline: string
    key_stats: {
      power_bracket: number
      bracket_label: string
      estimated_win_turn: number
      primary_engine: string
      primary_win_condition: string
      tribal?: Record<string, unknown>
    }
    strengths: string[]
    weaknesses: string[]
    suggested_actions: string[]
    bracket_mismatch?: { requested: number; actual: number; reason: string }
    /** Criteria the builder could not satisfy, e.g. an include_cards name
     *  outside the commander's colour identity. `criteria` is the machine tag
     *  ("include_card"), `detail` the human sentence. */
    unmet_criteria?: { criteria: string; detail: string }[]
  }
  deck: {
    commander: string
    color_identity: string[]
    card_count: number
    build_axis?: string
    decklist?: string
    cards: DeckCard[]
    build_notes?: string[]
  }
  analysis: {
    /** Same marker as the root one, at the analysis level (3-api.1). */
    analysis_locale?: Exclude<Lang, 'en'>
    validation?: Record<string, unknown>
    ipom: Record<string, unknown>
    bracket: Record<string, unknown>
    win_conditions: Record<string, unknown>
    commander_synergy?: Record<string, unknown>
    /** Additive, and NESTED under `analysis` - the API put it on
     *  AnalysisModel (`deck_stats: Optional[Dict]`), NOT at the response root
     *  where analyze-complete carries its own `deck_stats`. Only API builds
     *  that ship the block send it at all. buildAnalysisFromResult forwards it
     *  into the synthesized deck_stats WHEN PRESENT and omits the key
     *  otherwise, so the stat band self-gates to null on /build until then. */
    // eslint-disable-next-line i18next/no-literal-string -- type import path
    deck_stats?: { composition?: import('./types').DeckComposition | null }
    /** At-the-table blocks, NESTED under `analysis` for the same reason
     *  deck_stats is: the API hangs them off AnalysisModel, not the response
     *  root. Absent on any build from an API older than 2026-08-10, and
     *  individually absent/null through their own server-side gates, so
     *  buildAnalysisFromResult forwards each only when present and the band
     *  keeps self-gating to nothing otherwise. */
    // eslint-disable-next-line i18next/no-literal-string -- type import path
    deck_health?: import('./types').DeckHealth | null
    // eslint-disable-next-line i18next/no-literal-string -- type import path
    archenemy?: import('./types').Archenemy
    // eslint-disable-next-line i18next/no-literal-string -- type import path
    inclusion_gap?: import('./types').InclusionGap
  }
  mechanics_detected?: Record<string, number>
  // eslint-disable-next-line i18next/no-literal-string -- type import path
  mechanics_detected_v2?: import('./types').MechanicDetectedV2[]
  // Explicitly nullable: the API declares it Optional with no
  // response_model_exclude_none, so an under-budget build sends `null` rather
  // than omitting the key.
  budget_note?: string | null
  // Additive server-computed ownership rollup (owned counts, upgrade cost, buy
  // list). Absent when no collection signal was in the request; callers fall
  // back to client-side name matching. See selectOwnershipView.
  ownership_summary?: OwnershipSummary | null
  // Present only when the commander (or partner) is from a set that has not
  // released yet. The deck is fully built and every other card in it is legal;
  // the deck simply is not playable until this date. Null on normal builds.
  upcoming_legality?: UpcomingLegality | null
  schema_versions: Record<string, string>
}

export interface UpcomingLegality {
  /** ISO date (UTC). The LATER date when both commander and partner are upcoming. */
  legal_from: string
  set_name: string
  /** The command-zone cards that are not legal yet - commander, partner, or both. */
  cards: string[]
}

export function useBuildAndAnalyze() {
  return useMutation({
    mutationFn: (req: BuildAndAnalyzeRequest) =>
      apiPost<BuildAndAnalyzeResult>('/commander/build-and-analyze', req),
  })
}

// --- Bracket Adjustment ---
// Canonical /decks/adjust-bracket contract (schema_version 1). The endpoint
// returns verified cut/add swap pairs that move a deck to a target bracket.

export interface AdjustBracketRequest {
  decklist: string
  target_bracket: number
  commander?: string
  partner?: string
  budget_per_card?: number
  // Ownership contract at parity with /commander/build-and-analyze so an
  // adjusted deck sources swaps the way the builder builds. ownership_mode wins
  // when set (off/prefer/only); prefer_owned/collection_only are the legacy flags.
  prefer_owned?: boolean
  collection_only?: boolean
  ownership_mode?: 'off' | 'prefer' | 'only'
  collection_id?: string
  additional_owned?: string[]
  // Cards the engine may never cut. Server cap is 30 names.
  protected_cards?: string[]
  // Total spend ceiling across every ADD in the response. The server drops
  // swaps until the sum of add.price_usd fits, and reports what it dropped in
  // budget_summary. Omit the key entirely for no total cap.
  total_budget?: number
  // Never add a WotC Game Changer, whatever the target bracket asks for.
  exclude_game_changers?: boolean
  // Names the engine may never ADD (e.g. cards already committed to the user's
  // other decks). Server cap is 2000 names.
  exclude_cards?: string[]
}

// Public category enum stamped on each cut/add. Internal names are mapped to
// these at the API serialization boundary.
export type SwapCategory =
  | 'synergy'
  | 'combo'
  | 'tutor'
  | 'power'
  | 'package'
  | 'cedh_staple'
  | 'game_changer'
  | 'mld'
  | 'extra_turns'
  | 'fast_mana'

// Why the engine picked this card. Every member is optional: the server only
// emits what it knows, older/flag-off builds omit the whole object, and a
// partial payload must render whatever it does carry.
export interface SwapEvidence {
  synergy?: {
    // Commander synergy of the card. Emitted as a 0-1 fraction; the UI also
    // tolerates a 0-100 value so a server scale change cannot print "4200%".
    score: number
    source: 'edhrec' | 'theme' | 'none'
    reason: string
  }
  role?: {
    primary: string | null
    engine_contribution: number
  }
  engine_affinity?: number
  staple_rank?: number | null
  // Deck-composition floor the cut is measured against (e.g. do not cut below
  // the ramp floor). Null when the card is not in a quota category.
  quota?: {
    category: 'ramp' | 'removal' | 'wipe' | 'draw' | null
    deck_count: number
    floor: number
    at_floor: boolean
  } | null
}

export interface SwapCard {
  name: string
  reason: string
  category: SwapCategory
  price_usd?: number | null
  owned?: boolean | null
  // True when this card is a WotC Game Changer (mainly on adds, occasionally on
  // cuts). Surfaced as a distinctive chip so the user always sees it.
  is_game_changer?: boolean
  // Structured "why this card" data behind the reason string. Optional.
  evidence?: SwapEvidence
}

// An alternate CUT candidate for a swap's slot: the add stays the same, only
// the card leaving the deck changes.
export interface SwapAlternate {
  name: string
  reason: string
  category: SwapCategory
  evidence?: SwapEvidence
}

export interface SwapPair {
  // The contract says cut is always present, but a malformed entry must not
  // crash the client (a phone hit `undefined is not an object` on cut.category),
  // so this is typed optional to force guards at every access.
  cut?: SwapCard | null
  // Null on a pure removal (a floor-forced cut with no replacement).
  add: SwapCard | null
  // Combo-line swaps share a group_id so clients accept/skip them atomically.
  group_id: string | null
  // Per-swap caveat from the server (e.g. "cutting this drops you to the ramp
  // floor"). Rendered as an amber note on the row.
  warning?: string
  // Up to 3 alternate cut candidates for this slot. Absent on older servers.
  alternates?: SwapAlternate[]
}

// Reasons the target bracket could not be reached. Null when it was reached.
export type UnmetReason =
  | 'budget_capped'
  | 'insufficient_on_identity_power'
  | 'swap_cap_reached'
  // The deck's badge would equal the target only via a WotC policy floor (e.g.
  // a Game Changer floor) while its actual speed is still outside the band.
  | 'floor_badge_only'
  // The commander's own engine floors the deck above a low target, so no card
  // selection can reach it. Fires on e.g. target Bracket 1 for a fast commander.
  | 'commander_inherent_speed'
  // --- DESCEND-only, added 2026-08-02 (API dda42c5) -------------------------
  // Before this, every one of these came back as insufficient_on_identity_power,
  // which is a CLIMB concept ("the on-identity pool can't supply enough power to
  // go UP") and was simply the server's catch-all default on a down-adjust.
  // The same commander floor as commander_inherent_speed, except the descend
  // could not bring the deck down even to that floor.
  | 'commander_floor_unreachable'
  // Cuts moved the bracket closer to the target without reaching it (e.g. B5 to
  // B4 while targeting B2). Comes with NON-EMPTY swaps - the deck did change.
  | 'partial_progress_bracket'
  // The badge did not move, but the cuts made the deck measurably slower.
  // Also comes with NON-EMPTY swaps.
  | 'partial_progress_speed_only'
  // No cut moved the deck toward the target, so it was returned unchanged with
  // ZERO swaps. Deliberately coarse on the server: it does not yet distinguish
  // "nothing was cuttable" from "every replacement was rejected".
  | 'no_effective_cut'

export interface AdjustBracketResult {
  schema_version: number
  current_bracket: number
  target_bracket: number
  achieved_bracket: number
  target_reached: boolean
  unmet_reason: UnmetReason | null
  // The engine's own prose for WHY the target was missed (e.g. "Built the
  // strongest on-identity climb I can; it lands at Bracket 3 (~turn 6.8)").
  // Null when the target was reached; absent on older servers.
  unmet_explanation?: string | null
  swaps: SwapPair[]
  swap_count: number
  swap_cap_hit: boolean
  floors_triggered: string[]
  // Contents-vs-speed fit of the adjusted deck (same shape the analyzer renders).
  // Present on every response; status "matched" means nothing to surface.
  bracket_fit: BracketFit
  updated_decklist: string
  summary: string
  commander: string | null
  partner: string | null
  // Correlates this response with the server's adjust telemetry. Rides along on
  // beta feedback so a thumbs-down joins back to the exact run. Optional.
  request_id?: string
  // Present ONLY when total_budget was supplied. Absent (or null) means no
  // total cap was in force, which is not the same as "spent nothing".
  budget_summary?: {
    total_budget: number
    spent_usd: number
    dropped_swaps: number
  } | null
}

export function useAdjustBracket() {
  return useMutation({
    mutationFn: (req: AdjustBracketRequest) =>
      apiPost<AdjustBracketResult>('/decks/adjust-bracket', req),
  })
}

// --- Cut to 100 (deck trim) ---
// Canonical /decks/trim contract (schema_version 1). Ranks the weakest cards in
// an oversized deck so the user can get back to a legal 100. Errors (400) on a
// target_size other than 100, a deck over 150 cards, or an unparseable list.

export interface TrimDeckRequest {
  decklist: string
  commander?: string
  partner?: string
  // Cards the ranking may never pick. Same contract as adjust's protected list.
  protected_cards?: string[]
  // Only 100 is accepted today; the field exists so a future size needs no new
  // wire shape.
  target_size?: number
}

// One ranked cut. `category` is the composition bucket the card sits in, which
// is what makes "you are cutting your fourth wipe" legible; `evidence` is the
// SAME shape the adjust panel already renders behind a swap's cut.
export interface TrimCut {
  name: string
  reason: string
  category: string
  evidence: SwapEvidence | null
  is_land: boolean
}

export interface TrimDeckResult {
  schema_version: number
  request_id: string | null
  current_count: number
  target_count: number
  cuts_needed: number
  // Exactly the cuts that reach the target, in ranked order.
  cuts: TrimCut[]
  // Ranked replacements for any cut the user keeps instead. May be empty.
  spare_cuts: TrimCut[]
  cut_count: number
  updated_decklist: string
  summary: string
  commander: string | null
  partner: string | null
  // Set when the deck could not supply enough safe cuts to reach the target.
  // The cuts it DID find still ship.
  unmet_reason: 'insufficient_cuttable' | null
}

export function useTrimDeck() {
  return useMutation({
    mutationFn: (req: TrimDeckRequest) => apiPost<TrimDeckResult>('/decks/trim', req),
  })
}

// --- Natural Language Build ---

// --- Mana Tune ("Fix my mana") ---

export interface ManaTuneRequest {
  decklist: string
  commander?: string
  partner?: string
  budget_per_card?: number
  prefer_owned?: boolean
  collection_only?: boolean
  ownership_mode?: 'off' | 'prefer' | 'only'
  collection_id?: string
  additional_owned?: string[]
  protected_cards?: string[]
  max_swaps?: number
}

export type ManaTuneUnmetReason =
  | 'manabase_healthy'
  | 'no_in_budget_fixing'
  | 'bulk_unavailable'

export interface ManaColorRow {
  pips: number
  sources: number
  ratio: number
  needed_sources: number
  deficit_sources: number
  surplus_sources: number
  // False for a splash the server does not judge (under 3 pips). The row still
  // ships so the table can show the color without implying a problem.
  judged: boolean
}

export interface ManaNamedCard {
  name: string
  quantity: number
}

export interface ManaTuneDiagnosis {
  land_count: number
  land_target: number
  recommended_land_range: [number, number]
  color_target_ratio: number
  colors: Record<string, ManaColorRow>
  colors_in_deficit: string[]
  tapped_unconditional: ManaNamedCard[]
  untapped_ratio: number
  colorless_drag: ManaNamedCard[]
  fetch_gap: ManaNamedCard[]
  commander_castability: number
}

// Additive EDHREC context the server attaches to every mana-tune cut and add.
// `line` is a ready-to-render sentence ("In 78% of Miirym lists on EDHREC,
// +0.31 synergy" / "Not on Miirym's EDHREC page"), so the client never formats
// the numbers itself. The whole object is optional and `line` is nullable: an
// older server omits the field, and a card the server has no line for sends
// null. Both render nothing.
export interface ManaTuneEdhrec {
  inclusion_pct: number | null
  synergy: number | null
  on_page: boolean
  line: string | null
}

export interface ManaTuneCut {
  name: string
  reason: string
  category: string
  edhrec?: ManaTuneEdhrec | null
}

export interface ManaTuneAdd {
  name: string
  reason: string
  category: string
  price_usd: number | null
  owned: boolean
  produced_mana: string[]
  enters_tapped: boolean
  edhrec?: ManaTuneEdhrec | null
}

export interface ManaTuneSwap {
  // The contract says both sides are always present, but a malformed entry must
  // not crash the client (the same phone crash BracketAdjust's SwapPair was
  // hardened against), so both are typed optional to force guards at every
  // access. The UI drops any entry that does not name both sides.
  cut?: ManaTuneCut | null
  add?: ManaTuneAdd | null
}

export interface ManaTuneResult {
  schema_version: number
  request_id?: string
  diagnosis: ManaTuneDiagnosis
  swaps: ManaTuneSwap[]
  swap_count: number
  // Lands to remove with no replacement (the deck is over its land range).
  cuts_only: ManaTuneCut[]
  adds_only: unknown[]
  ownership_summary: OwnershipSummary | null
  updated_decklist: string
  summary: string
  commander: string | null
  partner: string | null
  unmet_reason: ManaTuneUnmetReason | null
}

export function useManaTune() {
  return useMutation({
    mutationFn: (req: ManaTuneRequest) =>
      apiPost<ManaTuneResult>('/decks/mana-tune', req),
  })
}

// --- Rules Chat ---

export function useRulesChat() {
  return useMutation({
    mutationFn: (req: RulesChatRequest) =>
      apiPost<RulesChatResponse>('/rules/chat', req),
  })
}

export function useIdentifyCardFromImage() {
  return useMutation({
    mutationFn: (req: { image_base64: string; media_type: string }) =>
      apiPost<IdentifyCardResponse>('/rules/identify-card', req),
  })
}
